import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const EngagementMatrix = () => {
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('week');

  const channels = [
    { id: 'email', label: 'Email', icon: 'Mail' },
    { id: 'phone', label: 'Téléphone', icon: 'Phone' },
    { id: 'meeting', label: 'Réunions', icon: 'Calendar' },
    { id: 'chat', label: 'Chat', icon: 'MessageSquare' },
    { id: 'social', label: 'Réseaux sociaux', icon: 'Share2' }
  ];

  const timeframes = [
    { value: 'day', label: 'Jour' },
    { value: 'week', label: 'Semaine' },
    { value: 'month', label: 'Mois' }
  ];

  const engagementData = [
    { channel: 'email', day: 'Lun', value: 85, interactions: 342 },
    { channel: 'email', day: 'Mar', value: 92, interactions: 368 },
    { channel: 'email', day: 'Mer', value: 78, interactions: 312 },
    { channel: 'email', day: 'Jeu', value: 88, interactions: 352 },
    { channel: 'email', day: 'Ven', value: 95, interactions: 380 },
    { channel: 'phone', day: 'Lun', value: 65, interactions: 156 },
    { channel: 'phone', day: 'Mar', value: 72, interactions: 173 },
    { channel: 'phone', day: 'Mer', value: 68, interactions: 163 },
    { channel: 'phone', day: 'Jeu', value: 75, interactions: 180 },
    { channel: 'phone', day: 'Ven', value: 70, interactions: 168 },
    { channel: 'meeting', day: 'Lun', value: 45, interactions: 89 },
    { channel: 'meeting', day: 'Mar', value: 52, interactions: 103 },
    { channel: 'meeting', day: 'Mer', value: 48, interactions: 95 },
    { channel: 'meeting', day: 'Jeu', value: 55, interactions: 109 },
    { channel: 'meeting', day: 'Ven', value: 42, interactions: 83 },
    { channel: 'chat', day: 'Lun', value: 88, interactions: 267 },
    { channel: 'chat', day: 'Mar', value: 90, interactions: 273 },
    { channel: 'chat', day: 'Mer', value: 85, interactions: 258 },
    { channel: 'chat', day: 'Jeu', value: 92, interactions: 279 },
    { channel: 'chat', day: 'Ven', value: 87, interactions: 264 },
    { channel: 'social', day: 'Lun', value: 35, interactions: 124 },
    { channel: 'social', day: 'Mar', value: 38, interactions: 135 },
    { channel: 'social', day: 'Mer', value: 42, interactions: 149 },
    { channel: 'social', day: 'Jeu', value: 40, interactions: 142 },
    { channel: 'social', day: 'Ven', value: 45, interactions: 160 }
  ];

  const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];

  const getHeatColor = (value) => {
    if (value >= 80) return 'bg-success';
    if (value >= 60) return 'bg-secondary';
    if (value >= 40) return 'bg-warning';
    return 'bg-error';
  };

  const getHeatOpacity = (value) => {
    if (value >= 80) return 'opacity-100';
    if (value >= 60) return 'opacity-75';
    if (value >= 40) return 'opacity-50';
    return 'opacity-30';
  };

  const handleCellClick = (channel, day, data) => {
    setSelectedChannel({ channel, day, ...data });
  };

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-elevation-2">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">
            Matrice d'engagement
          </h2>
          <p className="text-sm text-muted-foreground">
            Modèles d'interaction par canal de communication
          </p>
        </div>
        
        <div className="flex items-center gap-2 overflow-x-auto">
          {timeframes?.map((tf) => (
            <button
              key={tf?.value}
              onClick={() => setSelectedTimeframe(tf?.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth whitespace-nowrap flex-shrink-0 ${
                selectedTimeframe === tf?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {tf?.label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          <div className="grid grid-cols-6 gap-2 mb-2">
            <div className="text-xs font-medium text-muted-foreground"></div>
            {days?.map((day) => (
              <div key={day} className="text-xs font-medium text-center text-muted-foreground">
                {day}
              </div>
            ))}
          </div>

          {channels?.map((channel) => (
            <div key={channel?.id} className="grid grid-cols-6 gap-2 mb-2">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Icon name={channel?.icon} size={16} />
                <span className="hidden md:inline">{channel?.label}</span>
              </div>
              
              {days?.map((day) => {
                const data = engagementData?.find(
                  (d) => d?.channel === channel?.id && d?.day === day
                );
                return (
                  <button
                    key={`${channel?.id}-${day}`}
                    onClick={() => handleCellClick(channel?.id, day, data)}
                    className={`h-12 md:h-16 rounded-lg ${getHeatColor(data?.value || 0)} ${getHeatOpacity(data?.value || 0)} hover:scale-105 transition-smooth cursor-pointer relative group`}
                    title={`${channel?.label} - ${day}: ${data?.interactions || 0} interactions`}
                  >
                    <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-smooth">
                      {data?.value || 0}%
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {selectedChannel && (
        <div className="mt-6 p-4 bg-muted rounded-lg">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">
                Détails de l'engagement
              </h3>
              <p className="text-xs text-muted-foreground">
                {channels?.find(c => c?.id === selectedChannel?.channel)?.label} - {selectedChannel?.day}
              </p>
            </div>
            <button
              onClick={() => setSelectedChannel(null)}
              className="p-1 hover:bg-background rounded transition-smooth"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Score d'engagement</p>
              <p className="text-lg font-semibold text-foreground">{selectedChannel?.value}%</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total interactions</p>
              <p className="text-lg font-semibold text-foreground">{selectedChannel?.interactions}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Taux de réponse</p>
              <p className="text-lg font-semibold text-foreground">
                {Math.round(selectedChannel?.value * 0.85)}%
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 flex items-center justify-center gap-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-success"></div>
          <span className="text-xs text-muted-foreground">Élevé (80%+)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-secondary opacity-75"></div>
          <span className="text-xs text-muted-foreground">Moyen (60-79%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-warning opacity-50"></div>
          <span className="text-xs text-muted-foreground">Faible (40-59%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-error opacity-30"></div>
          <span className="text-xs text-muted-foreground">Très faible (&lt;40%)</span>
        </div>
      </div>
    </div>
  );
};

export default EngagementMatrix;